import { HttpClient } from '@angular/common/http';
import { Component, computed, inject, Injectable, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDialogService, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { ImageCroppedEvent, ImageCropperModule } from 'ngx-image-cropper';
import { lastValueFrom } from 'rxjs';

import { FiscalPrinters, restManager, util } from 'app/ajs-upgraded-providers';

import { ImagesManagerService, ItemThumbnailType } from 'src/app/core/services/images-manager';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';

import { IP_V4_PATTERN } from '../../registry/settings-patterns';

/** The files of the logo: the image in the format of the printer, its preview, and the image that was cropped */
const LOGO_PRINTER_FILE = 'logo_small.bin';
const LOGO_PREVIEW_FILE = 'logo_small.png';
const LOGO_ORIGINAL_FILE = 'logo_original.png';

/** The width of the logo, in dots, and its brightness: the values that the server accepts */
const LOGO_WIDTHS = ['150', '200', '250', '300', '350', '400', '450'];
const LOGO_BRIGHTNESSES = Array.from({ length: 16 }, (_, index) => String(index + 1));

/** The code that the server answers when the logo does not fit the memory of the printer */
const IMAGE_TOO_BIG_ERROR_CODE = 607;

/**
 * Dialog to set the logo that the printers print on the receipts. The user chooses an image and crops it, the server turns it
 * into the format of the printer (with the width and the brightness chosen), and the result can be tested on a printer before
 * it is saved.
 *
 * Steps, as in the legacy dialog: no logo → image chosen and cropped → logo created (preview) → test (optional) → saved.
 */
@Component({
    selector: 'app-logo-editor-dialog',
    standalone: true,
    imports: [
        ActionBtnComponent,
        FormsModule,
        ImageCropperModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TranslateModule
    ],
    templateUrl: './logo-editor-dialog.component.html'
})
export class LogoEditorDialogComponent implements OnInit {
    private readonly dialogRef = inject(MatDialogRef<LogoEditorDialogComponent>);
    private readonly fiscalPrinters = inject(FiscalPrinters);
    private readonly http = inject(HttpClient);
    private readonly imagesManagerService = inject(ImagesManagerService);
    private readonly restManagerService = inject(restManager);
    private readonly snackBar = inject(MatSnackBar);
    private readonly translateService = inject(TranslateService);
    private readonly utilService = inject(util);

    /** The types of printer for the test: the value is the type that the driver of the fiscal printers knows (an Epson MF printer is "fiscal") */
    protected readonly testTypes = [{ value: 'rt', label: 'RT' }, { value: 'fiscal', label: 'MF' }];
    protected readonly widths = LOGO_WIDTHS;
    protected readonly brightnesses = LOGO_BRIGHTNESSES;

    /** The logo that is saved now, if there is one */
    protected readonly currentLogo = signal<string | null>(null);
    /** The image chosen by the user, to be cropped */
    protected readonly selectedFile = signal<File | null>(null);
    /** The part of the image chosen, as a data URL */
    protected readonly croppedImage = signal<string | null>(null);
    /** The logo made by the server: the preview, the data for the printer, and the image it was made from */
    protected readonly editedLogo = signal<string | null>(null);
    protected readonly finalLogo = signal<string | null>(null);
    protected readonly originalLogo = signal<string | null>(null);
    protected readonly loading = signal(false);
    protected readonly errorMessage = signal<string | null>(null);

    protected width = '400';
    protected brightness = '7';
    protected testIp = '';
    protected testType = 'rt';

    protected readonly isTestIpValid = () => IP_V4_PATTERN.test(this.testIp);

    /** The user can confirm when a logo has been made and nothing is running */
    protected readonly canConfirm = computed(() => !!this.editedLogo() && !!this.finalLogo() && !this.loading());
    protected readonly showDropZone = computed(() => !this.selectedFile() && !this.editedLogo() && !this.currentLogo());

    async ngOnInit() {
        try {
            const url = await this.imagesManagerService.getImage(LOGO_PREVIEW_FILE);

            // The address is used only if the file can be read
            await lastValueFrom(this.http.get(url, { responseType: 'blob' }));

            // The user may have chosen an image while the logo was loading: the saved logo must not appear next to it
            if (!this.selectedFile() && !this.editedLogo()) {
                this.currentLogo.set(url);
            }
        } catch {
            this.currentLogo.set(null);
        }
    }

    protected onFileInput(event: Event) {
        const input = event.target as HTMLInputElement;

        this.selectFile(input.files?.[0]);
        input.value = '';
    }

    protected onDrop(event: DragEvent) {
        event.preventDefault();
        this.selectFile(event.dataTransfer?.files?.[0]);
    }

    protected onImageCropped(event: ImageCroppedEvent) {
        this.croppedImage.set(event.base64 ?? null);
    }

    protected onLoadImageFailed() {
        this.selectedFile.set(null);
        this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_IMAGE');
    }

    /** Makes the logo from the part of the image that was cropped */
    protected createLogo() {
        const cropped = this.croppedImage();

        if (cropped) {
            return this.generateLogo(cropped, cropped);
        }
    }

    /** Makes the logo again from its preview, e.g. with another brightness */
    protected regenerateLogo() {
        const preview = this.editedLogo();

        if (preview) {
            return this.generateLogo(preview, this.originalLogo());
        }
    }

    /** Throws away the image and the logo that are being made, to start again */
    protected resetEdits() {
        this.selectedFile.set(null);
        this.croppedImage.set(null);
        this.editedLogo.set(null);
        this.finalLogo.set(null);
        this.originalLogo.set(null);
        this.currentLogo.set(null);
        this.errorMessage.set(null);
        this.loading.set(false);
    }

    /**
     * Removes the logo that is saved. As in the legacy dialog the files are only marked as deleted: `saveAllImages` drops them
     * without asking the server to delete them, so the logo is not removed from the server.
     */
    protected async deleteLogo() {
        const files: ItemThumbnailType[] = [LOGO_PRINTER_FILE, LOGO_PREVIEW_FILE, LOGO_ORIGINAL_FILE].map(name => ({ deleted: true, image_url: name }));

        try {
            await this.imagesManagerService.saveAllImages(files);
        } catch {
            // The dialog shows no logo anyway, as the legacy one does
        }

        this.resetEdits();
    }

    /** Prints the logo on a printer to see how it looks */
    protected async testLogo() {
        const finalLogo = this.finalLogo();

        if (!finalLogo || !this.isTestIpValid()) {
            return;
        }

        // The answer of the previous test is not valid any more
        this.errorMessage.set(null);
        this.loading.set(true);

        try {
            await this.fiscalPrinters.printLogo(this.utilService.dataURLToBase64(finalLogo), this.testIp, this.testType);
        } catch {
            this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_TEST');
        } finally {
            this.loading.set(false);
        }
    }

    protected async confirm() {
        const [finalLogo, editedLogo, originalLogo] = [this.finalLogo(), this.editedLogo(), this.originalLogo()];

        if (!finalLogo || !editedLogo || !originalLogo) {
            return;
        }

        this.errorMessage.set(null);
        this.loading.set(true);

        const files: ItemThumbnailType[] = [
            { uploaded: false, name: LOGO_PRINTER_FILE, data: this.utilService.dataURLToBlob(finalLogo) },
            { uploaded: false, name: LOGO_PREVIEW_FILE, data: this.utilService.dataURLToBlob(editedLogo) },
            { uploaded: false, name: LOGO_ORIGINAL_FILE, data: this.utilService.dataURLToBlob(originalLogo) }
        ];

        try {
            await this.imagesManagerService.saveAllImages(files);
            this.snackBar.open(this.translateService.instant('SETTINGS.HUB.LOGO_EDITOR.SAVED'), undefined, { duration: 3000 });
            this.dialogRef.close(true);
        } catch {
            this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_SAVE');
        } finally {
            this.loading.set(false);
        }
    }

    private selectFile(file?: File) {
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_IMAGE');
            return;
        }

        this.errorMessage.set(null);
        this.croppedImage.set(null);
        this.selectedFile.set(file);
    }

    /** Asks the server to turn an image into the logo for the printer, with the width and the brightness chosen */
    private async generateLogo(dataUri: string, original: string | null) {
        this.errorMessage.set(null);
        this.loading.set(true);

        try {
            const result = await this.restManagerService.post('tools/epson_logo', { data_uri: dataUri }, null, { 'logo-width': this.width, brightness: this.brightness });

            if (result?.final_logo && result?.edited_logo) {
                this.editedLogo.set(this.utilService.base64ToDataURL('image/png', result.edited_logo));
                this.finalLogo.set(this.utilService.base64ToDataURL('image/bin', result.final_logo));
                this.originalLogo.set(original);
                this.selectedFile.set(null);
                this.croppedImage.set(null);
            } else {
                // The server answered without the logo: the user has to know, and keeps the image to try again (the frame starts again from the whole image)
                this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_GENERATE');
            }
        } catch (error: any) {
            const serverError = error?.data?.error;

            if (serverError?.code === IMAGE_TOO_BIG_ERROR_CODE) {
                this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_TOO_BIG');
            } else if (serverError?.message) {
                this.errorMessage.set(serverError.message);
            } else {
                this.showError('SETTINGS.HUB.LOGO_EDITOR.ERROR_GENERATE');
            }
        } finally {
            this.loading.set(false);
        }
    }

    private showError(key: string) {
        this.errorMessage.set(this.translateService.instant(key));
    }
}

@Injectable({ providedIn: 'root' })
export class LogoEditorDialogService extends BaseDialogService {
    private readonly dialog = inject(MatDialog);

    /** Resolves to true when a logo has been saved */
    public openDialog(): Promise<boolean | undefined> {
        return lastValueFrom(this.dialog.open(LogoEditorDialogComponent, { ...this.switchMobileDesktopDimensions(), disableClose: true }).afterClosed());
    }
}
